import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { AnalysisItem, CaseFormulationRevision } from '@/domain/schema/v1';
import {
  diffRevisions,
  FORMULATION_ITEM_FIELDS,
  FORMULATION_LABELS,
  FORMULATION_GROUPS,
  FORMULATION_HELP,
  FORMULATION_LIST_FIELDS,
  hypothesisTimeline,
  parsePendingOpinions,
  PENDING_KIND_LABEL,
  removePendingLine,
  type PendingOpinion,
} from '@/domain/workflow/formulation';
import { uuidv7 } from 'uuidv7';
import { useAutosave } from '../../hooks/useAutosave';
import { useLoad } from '../../hooks/useLoad';
import { useService } from '../../hooks/useService';
import { ConfirmButton, ItemsEditor, SaveIndicator, StringListEditor, TextArea } from '../../components/fields';
import type { ClientOutlet } from './ClientShell';
import { FormulationTidy } from './FormulationTidy';

const EVENT_LABEL: Record<string, string> = { added: '추가', strengthened: '강화', weakened: '약화', rejected: '폐기' };
const MARKS = ['strengthened', 'weakened', 'rejected'] as const;

/** 사례개념화 (MASTER §9): Revision으로 누적. 과거 버전은 지우지 않는다. */
export function FormulationScreen() {
  const { client } = useOutletContext<ClientOutlet>();
  const svc = useService();
  const { data, reload } = useLoad(async () => {
    const [revisions, sessions] = await Promise.all([
      svc.listFormulationRevisions(client.id),
      svc.listSessions(client.id),
    ]);
    return {
      revisions,
      sessions,
      current: await svc.currentFormulation(client.id),
      discardedList: await svc.discardedFormulationProposals(client.id),
    };
  }, [svc, client.id]);
  const [compare, setCompare] = useState<[string, string] | null>(null);
  const [discarded, setDiscarded] = useState<string | null>(null);
  if (!data) return <p className="muted">불러오는 중…</p>;
  const open = data.revisions.find((r) => r.status === 'proposal');
  const latestSession = data.sessions.filter((s) => s.status === 'approved').at(-1);

  return (
    <div className="stack">
      <p className="muted small">
        사례개념화는 회기마다 고쳐 가는 작업가설이에요. 외부 AI가 만든 개정안은 회기 진행의 4 검토에서도 승인할 수
        있어요.
      </p>
      {discarded && !open && (
        <p className="small" role="status">
          개정안을 버렸어요.{' '}
          <button
            type="button"
            className="link"
            onClick={() =>
              void svc.restoreFormulationProposal(discarded).then(() => {
                setDiscarded(null);
                reload();
              })
            }
          >
            되돌리기
          </button>
        </p>
      )}
      {open ? (
        <>
          {/* 개정안을 고칠 때는 화면 너비 전체를 쓰고, 현재 승인된 버전은 펼쳐 볼 수 있게 */}
          {data.current && (
            <details className="card">
              <summary>
                <strong>현재 승인된 사례개념화 v{data.current.version}</strong>{' '}
                <span className="muted small">펼쳐서 개정안과 비교</span>
              </summary>
              <RevisionView rev={data.current} title="" />
            </details>
          )}
          <FormulationTidy clientId={client.id} projectUrl={client.externalProjectUrl} onApplied={reload} />
          <ProposalEditor
            key={`${open.id}:${open.revision}`}
            rev={open}
            onApproved={reload}
            onDiscarded={(id) => {
              setDiscarded(id);
              reload();
            }}
          />
        </>
      ) : (
        <>
          {data.current ? (
            <RevisionView rev={data.current} title={`현재 사례개념화 v${data.current.version}`} />
          ) : (
            <div className="card">
              <strong>현재 사례개념화</strong>
              <p className="muted small">아직 승인된 사례개념화가 없어요.</p>
            </div>
          )}
          <div className="card">
            <strong>개정안</strong>
            <p className="muted small">지금 검토할 개정안이 없어요. 직접 고치려면 현재 버전에서 개정안을 만드세요.</p>
            <button
              type="button"
              className="primary"
              onClick={() =>
                void svc.createFormulationProposal(client.id, latestSession ? [latestSession.id] : []).then(reload)
              }
            >
              {data.revisions.length ? '새 개정안 만들기 (현재 버전에서 시작)' : '사례개념화 작성 시작'}
            </button>
          </div>
          <FormulationTidy clientId={client.id} projectUrl={client.externalProjectUrl} onApplied={reload} />
          {data.discardedList.length > 0 && (
            <div className="card">
              <strong>버린 개정안</strong>
              <ul className="list">
                {data.discardedList.map((r) => (
                  <li key={r.id} className="list-row">
                    <span>
                      v{r.version} · 버린 날 {r.deletedAt?.slice(0, 10)} · 항목{' '}
                      {FORMULATION_ITEM_FIELDS.reduce((n, f) => n + (r[f]?.length ?? 0), 0) +
                        FORMULATION_LIST_FIELDS.reduce((n, f) => n + (r[f]?.length ?? 0), 0)}
                      개
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        void svc.restoreFormulationProposal(r.id).then(() => {
                          setDiscarded(null);
                          reload();
                        })
                      }
                    >
                      되돌리기
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {data.revisions.length > 0 && (
        <div className="card">
          <h2>버전 이력</h2>
          <ul className="list">
            {[...data.revisions].reverse().map((r, i, arr) => (
              <li key={r.id} className="list-row">
                <span>
                  v{r.version} · {r.status === 'approved' ? '승인' : '제안'} · {r.createdAt.slice(0, 10)}
                  {r.changeReason ? ` · ${r.changeReason}` : ''}
                </span>
                {arr[i + 1] && (
                  <button type="button" onClick={() => setCompare([arr[i + 1]!.id, r.id])}>
                    이전과 비교
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {compare && (
        <Compare
          a={data.revisions.find((r) => r.id === compare[0])!}
          b={data.revisions.find((r) => r.id === compare[1])!}
        />
      )}
      {data.revisions.length > 0 && <Timeline revisions={data.revisions} />}
    </div>
  );
}

function ProposalEditor({
  rev,
  onApproved,
  onDiscarded,
}: {
  rev: CaseFormulationRevision;
  onApproved: () => void;
  onDiscarded: (id: string) => void;
}) {
  const svc = useService();
  const a = useAutosave<CaseFormulationRevision>(rev, (patch, r) => svc.updateFormulation(rev.id, patch, r));
  const v = a.value!;
  const hypotheses = FORMULATION_ITEM_FIELDS.flatMap((f) => v[f] ?? []).filter((i) => i.text.trim());
  const setItems = (f: (typeof FORMULATION_ITEM_FIELDS)[number], x: AnalysisItem[]) => {
    const before = new Set((v[f] ?? []).map((i) => i.id));
    const after = new Set(x.map((i) => i.id));
    const fresh = x.filter((i) => !before.has(i.id)).map((i) => i.id);
    const gone = [...before].filter((id) => !after.has(id));
    a.set(f, x);
    if (fresh.length) a.set('addedHypotheses', [...new Set([...(v.addedHypotheses ?? []), ...fresh])]);
    if (gone.length)
      for (const k of ['addedHypotheses', 'strengthenedHypotheses', 'weakenedHypotheses'] as const)
        if ((v[k] ?? []).some((id) => gone.includes(id)))
          a.set(
            k,
            (v[k] ?? []).filter((id) => !gone.includes(id)),
          );
  };
  /** 강화·약화·폐기 중 하나만 (그대로 = 셋 다 아님) */
  const mark = (id: string, m: (typeof MARKS)[number] | 'none') => {
    for (const k of MARKS) {
      const list = (v[`${k}Hypotheses`] ?? []).filter((x) => x !== id);
      a.set(`${k}Hypotheses`, k === m ? [...list, id] : list);
    }
  };
  return (
    <div className="card">
      <div className="card-head">
        <h2>개정안 v{rev.version} (제안)</h2>
        <SaveIndicator status={a.status} error={a.error} />
      </div>
      <PendingOpinions
        rev={v}
        onApply={(patch) => {
          for (const [k, val] of Object.entries(patch)) a.set(k as keyof CaseFormulationRevision, val as never);
        }}
      />
      <FieldIndex rev={v} />
      {FORMULATION_GROUPS.map((g) => (
        <section key={g.key} className="fm-group">
          <div className="fm-group-head">
            <h3>{g.title}</h3>
            <p className="muted small">{g.desc}</p>
          </div>
          {g.fields.map((f) => (
            <div key={f} id={`fm-${f}`} className="fm-field">
              <FieldHead field={f} count={(v[f] ?? []).length} />
              {isListField(f) ? (
                <StringListEditor label={FORMULATION_LABELS[f]} value={v[f]} onChange={(x) => a.set(f, x)} />
              ) : (
                <ItemsEditor bare label={FORMULATION_LABELS[f]} value={v[f]} onChange={(x) => setItems(f, x)} />
              )}
            </div>
          ))}
        </section>
      ))}
      {hypotheses.length > 0 && (
        <div className="field">
          <div className="field-label">
            <span>이번 개정에서 가설의 변화</span>
          </div>
          <p className="muted small" style={{ margin: '0 0 6px' }}>
            가설마다 하나만 고르세요. 강화·약화는 가설 이력과 사례보고서에 표시되고, 폐기하면 다음 버전부터
            빠져요(이력에는 남음). "새 가설"은 이번에 생긴 항목에 자동으로 붙어요.
          </p>
          <ul className="hyp-marks">
            {hypotheses.map((h) => {
              const cur = MARKS.find((m) => (v[`${m}Hypotheses`] ?? []).includes(h.id)) ?? 'none';
              return (
                <li key={h.id}>
                  <span className="hyp-text">
                    {(v.addedHypotheses ?? []).includes(h.id) && <span className="status-pill">새 가설</span>} {h.text}
                  </span>
                  <span className="seg-group" role="group" aria-label={`${h.text} 변화`}>
                    {(['none', ...MARKS] as const).map((m) => (
                      <button
                        key={m}
                        type="button"
                        className={cur === m ? `seg-btn on ${m}` : 'seg-btn'}
                        aria-pressed={cur === m}
                        onClick={() => mark(h.id, m)}
                      >
                        {m === 'none' ? '그대로' : EVENT_LABEL[m]}
                      </button>
                    ))}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <TextArea
        label="변경 이유 / 근거"
        value={v.changeReason}
        onChange={(x) => a.set('changeReason', x || undefined)}
      />
      <ConfirmButton
        className="primary"
        label="이 개정안 승인"
        confirmText="승인하면 이 버전이 현재 사례개념화가 됩니다. 이전 버전은 그대로 보존됩니다."
        onConfirm={() =>
          void a
            .flush()
            .then(() => svc.approveFormulation(rev.id))
            .then(onApproved)
        }
      />
      <ConfirmButton
        className="link small"
        label="이 개정안 버리기"
        confirmText="이 개정안을 버릴까요? 현재(승인된) 사례개념화는 그대로이고, 버린 직후에는 되돌릴 수 있어요."
        onConfirm={() =>
          void a
            .flush()
            .then(() => svc.discardFormulationProposal(rev.id))
            .then(() => onDiscarded(rev.id))
        }
      />
    </div>
  );
}

/**
 * AI가 강화·약화·폐기라고 했지만 기존 가설과 연결하지 못한 의견. 한 줄에 하나씩.
 * 기존 가설에 연결(가설 문장 전체를 보고 고름) / 새 가설로 추가 / 강점·자원(보호요인)으로 / 버리기.
 * 보호요인 = 내담자의 강점·자원 (어려움을 버티게 해 주는 것: 지지체계, 신앙공동체, 대처능력 등). 가설이 아니라 그 목록에 들어간다.
 * 처리한 직후에는 되돌릴 수 있다 (바뀐 칸을 처리 전 값으로).
 */
export function PendingOpinions({
  rev,
  onApply,
}: {
  rev: CaseFormulationRevision;
  onApply: (patch: Partial<CaseFormulationRevision>) => void;
}) {
  const items = parsePendingOpinions(rev.changeReason);
  const [picking, setPicking] = useState<string | null>(null);
  const [last, setLast] = useState<{ text: string; what: string; prev: Partial<CaseFormulationRevision> } | null>(null);
  if (!items.length && !last) return null;
  const hypotheses = FORMULATION_ITEM_FIELDS.flatMap((f) =>
    (rev[f] ?? []).filter((i) => i.text.trim()).map((i) => ({ ...i, field: f })),
  );
  const field = (k: PendingOpinion['kind']) => `${k}Hypotheses` as const;
  const done = (o: PendingOpinion, what: string, patch: Partial<CaseFormulationRevision>) => {
    const full = { ...patch, changeReason: removePendingLine(rev.changeReason, o.line) };
    const prev = Object.fromEntries(
      Object.keys(full).map((k) => {
        const old = rev[k as keyof CaseFormulationRevision];
        return [k, old === undefined ? (k === 'changeReason' ? '' : []) : structuredClone(old)];
      }),
    ) as Partial<CaseFormulationRevision>;
    setLast({ text: o.text, what, prev });
    setPicking(null);
    onApply(full);
  };
  const link = (o: PendingOpinion, id: string) =>
    done(o, '기존 가설에 연결', { [field(o.kind)]: [...new Set([...(rev[field(o.kind)] ?? []), id])] });
  const addNew = (o: PendingOpinion) => {
    const id = uuidv7();
    done(o, '새 가설로 추가', {
      beliefHypotheses: [...(rev.beliefHypotheses ?? []), { id, text: o.text, status: 'hypothesis' }],
      addedHypotheses: [...(rev.addedHypotheses ?? []), id],
      ...(o.kind === 'strengthened' ? { strengthenedHypotheses: [...(rev.strengthenedHypotheses ?? []), id] } : {}),
    });
  };
  return (
    <div className="warn-card card stack pending-opinions">
      <strong className="small">AI 의견 정리 ({items.length}) — 기존 가설과 자동으로 연결하지 못했습니다</strong>
      <span className="muted small">
        하나씩 고르거나, 많으면 "AI로 정리하기"로 기존 가설과 함께 한 번에 합치세요. "강점·자원"은 보호요인(지지체계,
        신앙, 대처능력처럼 내담자를 버티게 해 주는 것) 목록에 넣는다는 뜻이에요.
      </span>
      {last && (
        <p className="small undo-line" role="status">
          {last.what}: {last.text.length > 50 ? `${last.text.slice(0, 50)}…` : last.text}{' '}
          <button
            type="button"
            className="link"
            onClick={() => {
              onApply(last.prev);
              setLast(null);
            }}
          >
            되돌리기
          </button>
        </p>
      )}
      {items.map((o, n) => (
        <div key={o.line} className="opinion">
          <p className="opinion-text">
            <span className="opinion-no">{n + 1}</span>
            <span className="badge badge-review">{PENDING_KIND_LABEL[o.kind]}</span> {o.text}
          </p>
          <div className="row">
            {hypotheses.length > 0 && (
              <button
                type="button"
                className={picking === o.line ? 'on' : undefined}
                aria-expanded={picking === o.line}
                onClick={() => setPicking(picking === o.line ? null : o.line)}
              >
                기존 가설에 연결 ({PENDING_KIND_LABEL[o.kind]}) {picking === o.line ? '▴' : '▾'}
              </button>
            )}
            {o.kind !== 'rejected' && (
              <button type="button" onClick={() => addNew(o)}>
                새 가설로 추가
              </button>
            )}
            {o.kind === 'strengthened' && (
              <button
                type="button"
                title="가설이 아니라 보호요인(내담자의 강점·자원) 목록에 넣습니다"
                onClick={() =>
                  done(o, '강점·자원(보호요인)으로', { protectiveFactors: [...(rev.protectiveFactors ?? []), o.text] })
                }
              >
                강점·자원(보호요인)으로
              </button>
            )}
            <button type="button" className="link" onClick={() => done(o, '버림', {})}>
              버리기
            </button>
          </div>
          {picking === o.line && (
            <ul className="link-pick" aria-label="연결할 기존 가설">
              {hypotheses.map((h) => (
                <li key={h.id}>
                  <button type="button" onClick={() => link(o, h.id)}>
                    <span className="muted small">{FORMULATION_LABELS[h.field]}</span>
                    <span>{h.text}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

function RevisionView({ rev, title }: { rev: CaseFormulationRevision; title: string }) {
  return (
    <div className={title ? 'card stack fm-view' : 'stack fm-view'}>
      {title && <h2>{title}</h2>}
      {FORMULATION_GROUPS.map((g) => (
        <section key={g.key} className="fm-group">
          <div className="fm-group-head">
            <h3>{g.title}</h3>
          </div>
          {g.fields.map((f) => {
            const rows: { key: string; text: string; badge?: string }[] = isListField(f)
              ? (rev[f] ?? []).map((t, i) => ({ key: String(i), text: t }))
              : (rev[f] ?? []).map((x) => ({
                  key: x.id,
                  text: x.text,
                  badge: x.status === 'hypothesis' ? '가설' : x.status === 'observation' ? '관찰' : '확인됨',
                }));
            return (
              <div key={f} className="fm-field">
                <FieldHead field={f} count={rows.length} />
                {rows.length > 0 && (
                  <ul className="fm-list">
                    {rows.map((x) => (
                      <li key={x.key}>
                        {x.badge && <span className="badge badge-review">{x.badge}</span>} {x.text}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}

const isListField = (f: string): f is (typeof FORMULATION_LIST_FIELDS)[number] =>
  (FORMULATION_LIST_FIELDS as readonly string[]).includes(f);

/** 칸 제목: 이름 · 개수 · 무엇을 쓰는 칸인지 */
function FieldHead({ field, count }: { field: keyof typeof FORMULATION_LABELS; count: number }) {
  return (
    <div className="fm-field-head">
      <strong>{FORMULATION_LABELS[field]}</strong>
      <span className={count ? 'status-pill' : 'status-pill empty'}>{count ? `${count}개` : '비어 있음'}</span>
      <span className="muted small">{FORMULATION_HELP[field]}</span>
    </div>
  );
}

/** 칸 현황: 이름과 개수, 누르면 그 칸으로 */
function FieldIndex({ rev }: { rev: CaseFormulationRevision }) {
  return (
    <nav className="fm-index" aria-label="칸 바로가기">
      {FORMULATION_GROUPS.flatMap((g) => g.fields).map((f) => {
        const n = (rev[f] ?? []).length;
        return (
          <a key={f} href={`#fm-${f}`} className={n ? 'fm-chip' : 'fm-chip empty'}>
            {FORMULATION_LABELS[f]} {n}
          </a>
        );
      })}
    </nav>
  );
}

function Compare({ a, b }: { a: CaseFormulationRevision; b: CaseFormulationRevision }) {
  const d = diffRevisions(a, b);
  const label = { added: '추가', removed: '삭제', edited: '수정', status: '상태 변경' } as const;
  return (
    <div className="card">
      <h2>
        v{a.version} → v{b.version} 달라진 점
      </h2>
      {d.lists.length === 0 && d.items.length === 0 && <p className="muted">차이가 없습니다.</p>}
      <ul>
        {d.lists.flatMap((l) => [
          ...l.added.map((x) => (
            <li key={`${l.field}+${x}`}>
              + {FORMULATION_LABELS[l.field]}: {x}
            </li>
          )),
          ...l.removed.map((x) => (
            <li key={`${l.field}-${x}`}>
              − {FORMULATION_LABELS[l.field]}: {x}
            </li>
          )),
        ])}
        {d.items.map((i) => (
          <li key={`${i.field}${i.id}${i.change}`}>
            [{label[i.change]}] {FORMULATION_LABELS[i.field as keyof typeof FORMULATION_LABELS]}: {i.text}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Timeline({ revisions }: { revisions: CaseFormulationRevision[] }) {
  const t = hypothesisTimeline(revisions).filter((h) => h.events.length);
  if (!t.length) return null;
  return (
    <details className="card">
      <summary>
        <strong>가설의 흐름</strong>
      </summary>
      <ul>
        {t.map((h) => (
          <li key={h.id}>
            {h.text} — {h.events.map((e) => `v${e.version} ${EVENT_LABEL[e.event]}`).join(' → ')}
          </li>
        ))}
      </ul>
    </details>
  );
}
